import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError, patchJSON } from '@/lib/api'
import type { EventDetails } from './event-details-form'

interface EventDetailValues {
  name: string
  startsAt: string
  endsAt: string
  location: string
  description: string
  infoForAttendees: string
  eventContactName: string
  eventContactEmail: string
  capacity: string
  chapterId: number | null
  previousEditionId: number | null
}

export function useEventDetailsUpdate({
  event,
  values,
  onNotificationRequired,
  onSuccess,
}: {
  event: EventDetails
  values: EventDetailValues
  onNotificationRequired: () => void
  onSuccess: () => void
}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (notificationChoice?: boolean) =>
      patchJSON<{ event: EventDetails }>(`/api/v1/events/${event.id}`, {
        event: eventUpdatePayload(event, values, notificationChoice),
      }),
    onSuccess: ({ event: updated }) => {
      onSuccess()
      queryClient.setQueryData(['event', event.id], { event: updated })
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['registration-comparison', String(event.id)] })
    },
    onError: (error) => {
      if (isNotificationRequired(error)) onNotificationRequired()
    },
  })
}

function eventUpdatePayload(
  event: EventDetails,
  values: EventDetailValues,
  notificationChoice?: boolean,
) {
  const common = {
    location: values.location,
    description: values.description,
    info_for_attendees: values.infoForAttendees,
    event_contact_name: values.eventContactName,
    event_contact_email: values.eventContactEmail,
    capacity: values.capacity,
    ...(event.chapter !== undefined ? { chapter_id: values.chapterId } : {}),
    ...(values.previousEditionId !== (event.previous_edition_id ?? null)
      ? { previous_edition_id: values.previousEditionId }
      : {}),
  }
  if (event.status === 'draft') {
    return {
      name: values.name,
      starts_at: values.startsAt,
      ends_at: values.endsAt,
      ...common,
    }
  }
  return {
    ...(values.startsAt !== event.starts_at_local.slice(0, 16)
      ? { starts_at: values.startsAt }
      : {}),
    ...(values.endsAt !== (event.ends_at_local?.slice(0, 16) ?? '')
      ? { ends_at: values.endsAt }
      : {}),
    ...common,
    ...(notificationChoice === undefined ? {} : { notify_registrants: notificationChoice }),
  }
}

export function isNotificationRequired(error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 409) return false
  if (typeof error.body !== 'object' || error.body === null) return false

  return (error.body as { notification_required?: unknown }).notification_required === true
}
